import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, Table, Button, Badge, Modal, LoadingSkeleton } from '../components/ui';
import { PlusIcon, MagnifyingGlassIcon, EyeIcon, ArrowRightOnRectangleIcon, NoSymbolIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { safeErrorMessage } from '../utils/apiError';

function TenantList({ token }) {
  const [schools, setSchools] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusTarget, setStatusTarget] = useState(null);
  const [impersonateTarget, setImpersonateTarget] = useState(null);
  const [impersonateReason, setImpersonateReason] = useState('');
  const [busy, setBusy] = useState(false);

  const fetchSchools = useCallback(async () => {
    setLoading(true);
    try {
      const url = search ? `/api/v1/superadmin/schools?search=${encodeURIComponent(search)}` : '/api/v1/superadmin/schools';
      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (response.ok) {
        setSchools(await response.json());
      } else {
        setError('Failed to load schools');
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [token, search]);

  useEffect(() => { fetchSchools(); }, [fetchSchools]);

  const handleToggleStatus = async () => {
    if (!statusTarget) return;
    setBusy(true);
    setError('');
    const newStatus = statusTarget.status === 'active' ? 'suspended' : 'active';
    try {
      const response = await fetch(`/api/v1/superadmin/schools/${statusTarget.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus }),
      });
      if (response.ok) {
        const updated = await response.json();
        setSchools((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
        setStatusTarget(null);
      } else {
        setError(await safeErrorMessage(response, 'Failed to update school status'));
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleImpersonate = async () => {
    if (!impersonateTarget || !impersonateReason.trim()) return;
    setBusy(true);
    setError('');
    try {
      const response = await fetch(`/api/v1/superadmin/schools/${impersonateTarget.id}/impersonate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reason: impersonateReason.trim() }),
      });
      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('token', data.access_token);
        window.location.href = '/dashboard';
      } else {
        setError(await safeErrorMessage(response, 'Failed to start impersonation session'));
        setBusy(false);
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
      setBusy(false);
    }
  };

  const columns = [
    { header: 'Name', render: (row) => <strong>{row.name}</strong> },
    { header: 'Code', render: (row) => <code>{row.slug}</code> },
    { header: 'Status', render: (row) => <Badge variant={row.status === 'active' ? 'success' : 'danger'}>{row.status}</Badge> },
    { header: 'Created', render: (row) => (row.created_at ? new Date(row.created_at).toLocaleDateString() : '—') },
    {
      header: 'Actions',
      render: (row) => (
        <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
          <Link to={`/superadmin/schools/${row.id}`}>
            <Button variant="ghost" size="sm"><EyeIcon style={{ width: 14, height: 14 }} />View</Button>
          </Link>
          <Button variant="ghost" size="sm" onClick={() => { setImpersonateTarget(row); setImpersonateReason(''); }}>
            <ArrowRightOnRectangleIcon style={{ width: 14, height: 14 }} />Login As
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setStatusTarget(row)}>
            {row.status === 'active' ? <NoSymbolIcon style={{ width: 14, height: 14 }} /> : <CheckCircleIcon style={{ width: 14, height: 14 }} />}
            {row.status === 'active' ? 'Suspend' : 'Reactivate'}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-text)' }}>Schools</h2>
          <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginTop: '0.25rem' }}>
            Every school on the platform.
          </p>
        </div>
        <Link to="/superadmin/schools/new">
          <Button variant="primary"><PlusIcon style={{ width: 16, height: 16 }} />New School</Button>
        </Link>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div style={{ marginBottom: '1rem', position: 'relative', maxWidth: 320 }}>
        <MagnifyingGlassIcon style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: 'var(--color-text-muted)' }} />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or code..."
          style={{ width: '100%', padding: '0.5rem 0.75rem 0.5rem 2rem', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', fontSize: '0.85rem' }}
        />
      </div>

      <Card>
        {loading ? (
          <LoadingSkeleton type="text" rows={6} />
        ) : (
          <Table columns={columns} data={schools} emptyMessage="No schools found." />
        )}
      </Card>

      <Modal isOpen={Boolean(statusTarget)} onClose={() => setStatusTarget(null)} title={statusTarget ? `${statusTarget.status === 'active' ? 'Suspend' : 'Reactivate'} ${statusTarget.name}` : ''}>
        {statusTarget && (
          <div>
            <p style={{ fontSize: '0.9rem', color: 'var(--color-text-secondary)', marginBottom: '1.25rem' }}>
              {statusTarget.status === 'active'
                ? 'Staff at this school will be immediately unable to log in. Existing sessions remain valid until they expire.'
                : 'Staff at this school will be able to log in again.'}
            </p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button variant={statusTarget.status === 'active' ? 'danger' : 'primary'} onClick={handleToggleStatus} disabled={busy}>
                {busy ? 'Working...' : (statusTarget.status === 'active' ? 'Suspend School' : 'Reactivate School')}
              </Button>
              <Button variant="secondary" onClick={() => setStatusTarget(null)}>Cancel</Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal isOpen={Boolean(impersonateTarget)} onClose={() => setImpersonateTarget(null)} title={impersonateTarget ? `Login As — ${impersonateTarget.name}` : ''}>
        {impersonateTarget && (
          <div>
            <p style={{ fontSize: '0.9rem', color: 'var(--color-text-secondary)', marginBottom: '1rem' }}>
              This opens a 45-minute support session as this school's principal. A reason is required and is recorded in the audit log.
            </p>
            <div className="form-group">
              <label>Reason</label>
              <input
                type="text"
                value={impersonateReason}
                onChange={(e) => setImpersonateReason(e.target.value)}
                placeholder="e.g. Investigating support ticket #482"
                autoFocus
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
              <Button variant="primary" onClick={handleImpersonate} disabled={busy || !impersonateReason.trim()}>
                {busy ? 'Starting session...' : 'Start Session'}
              </Button>
              <Button variant="secondary" onClick={() => setImpersonateTarget(null)}>Cancel</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default TenantList;
